import React from 'react';
import { getSimpleNumber } from '../../utilities/numbers';
import { getHours } from '../../utilities/time';
import { useTranslation, Trans } from 'react-i18next';
import { Card } from '@llt-frontend/react-material-card';
import { Button } from '@llt-frontend/react-material-button';
import { useMediaQuery } from 'react-responsive';
import classnames from 'classnames';
import { QuoteListItemPropTypes } from './types';
import MEDIA_QUERIES from '../../constants/media-queries.json';
import dateFormat from 'dateformat';
import { formatPrice } from '../packages/repurchase/util';
import { useAnalyticsContext } from '@llt-frontend/react-analytics-context';
import { useLDClient } from 'launchdarkly-react-client-sdk';

function _formatDate(strValue) {
  const date = new Date(strValue);
  return dateFormat(date, 'dddd, mmmm dS h:MM TT Z');
}

// eslint-disable-next-line complexity

function shouldBePartOfExperiment() {
  const urlParams = new URLSearchParams(window?.location?.search);
  const clientIdUrl = urlParams.get('client_id') || null;
  const leadIdUrl = urlParams.get('lead_id') || null;

  return !!(leadIdUrl || clientIdUrl);
}

export function PackageListItem({ quote, item }) {
  const { trackEvent } = useAnalyticsContext();
  const ldClient = useLDClient();
  const { id: quoteId, name, ends_at, number_of_payments } = quote || {};
  const {
    name: itemName,
    meta: { list_price = {} } = {},
    packages: { duration_seconds, session_length_seconds } = {},
  } = item;
  const { quoted_price_cents } = quote.items && quote.items.length > 0 ? quote.items[0] : {};
  const hasSale = list_price.amount !== quoted_price_cents;
  const hasInstallments = number_of_payments > 1;
  const totalPrice = formatPrice(quoted_price_cents);
  const installmentPriceCents = quoted_price_cents / number_of_payments;
  const installmentPrice = formatPrice(installmentPriceCents);
  const oldPrice = formatPrice(list_price.amount);
  const priceOff = formatPrice(list_price.amount - quoted_price_cents);
  const hourlyPriceCents = session_length_seconds
    ? quoted_price_cents / getHours(duration_seconds)
    : undefined;
  const hourlyPrice = hourlyPriceCents ? formatPrice(hourlyPriceCents) : undefined;

  let buyLink = `/checkout?q=${quoteId}`;

  if (ldClient && quoteId) {
    const flag = ldClient.variation('ECOMM-621-package-quotes', 'control');
    if (flag.toLowerCase() === 'variant' && shouldBePartOfExperiment()) {
      buyLink = `/checkout?q=${quoteId}&package=true`;
    }
  }

  const wrapCTA = useMediaQuery(MEDIA_QUERIES.wrapCTA);

  const { t, ready: translationLoaded } = useTranslation('quotes/package-list-item');

  if (!translationLoaded) {
    return null;
  }

  function _getExpiry() {
    return (
      <div className="details__expiry">
        <div>{t('expiry', { date: _formatDate(ends_at) })}</div>
      </div>
    );
  }

  function trackClick() {
    trackEvent({ category: 'tutoring-packages', action: 'cta-click', label: 'package' });
    window['analytics']?.track?.('Element Clicked', {
      element_id: 'pkg-quote-cta-click',
      element_text: t('ctaButtonLabel'),
      element_type: 'button',
      page_path: '/quotes',
    });
  }

  return (
    <Card className="quotes-list-item">
      <div className="details">
        <h6 className="details__title">
          <strong>{name}</strong> - {itemName}
        </h6>

        <div className="details__sessions">
          {t('sessionsDetail', {
            sessionCount: getSimpleNumber(duration_seconds / session_length_seconds, 2),
            sessionLengthMinutes: getSimpleNumber(session_length_seconds / 60),
          })}
        </div>

        {!wrapCTA && _getExpiry()}
      </div>

      <div className="cta">
        {hasInstallments && (
          <div className="cta__installments">
            <Trans
              t={t}
              i18nKey="installments"
              components={{ strong: <strong className="cta__installments--total" /> }}
              values={{ number: number_of_payments, price: installmentPrice }}
            />
          </div>
        )}

        <div
          className={classnames('cta__price', {
            'cta__price--sale': hasSale,
            'cta__price--installments': hasInstallments,
          })}
        >
          <span className="cta__price-total">
            {hasInstallments ? t('totalPrice', { totalPrice }) : totalPrice}
          </span>
          {hourlyPrice && (
            <span className="cta__price-hourly">({t('perHour', { hourlyPrice })})</span>
          )}
        </div>

        {hasSale && (
          <div className="cta__price-off">
            <span className="cta__price-off-old">{oldPrice}</span>
            <span className="cta__price-off-amount">{t('off', { priceOff })}</span>
          </div>
        )}

        <div className="cta__button">
          <div className="cta__button-container">
            <Button onClick={trackClick} contained component="a" href={buyLink}>
              {t('ctaButtonLabel')}
            </Button>
          </div>
        </div>

        {wrapCTA && _getExpiry()}
      </div>
    </Card>
  );
}

PackageListItem.propTypes = QuoteListItemPropTypes;
